// src/components/Header/UserMenuPopover.jsx
import React from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Fade,
  Avatar,
  Badge,
  Switch,
  Stack,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import SchoolIcon from '@mui/icons-material/School';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { hasAllowedRole, splitRoles } from '../../utils/authSession';
import { ROLE_GROUPS } from '../../config/accessControl';

const UserMenuPopover = ({
  anchorEl,
  isOpen,
  onClose,
  userDetails,
  onLogoutClick,
  academicYear = 'N/A',
  notificationCount = 0,
  onThemeToggle,
  isDarkMode,
  onNotificationsOpen,
  onAcademicYearClick,
}) => {
  const navigate = useNavigate();

  // Helper functions
  const getFullName = () => {
    const { firstName, lastName } = userDetails;
    return lastName ? `${firstName} ${lastName}` : firstName;
  };

  const formatRoleName = (role) => {
    if (!role) return '';
    return role
      .toLowerCase()
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const userRoles = splitRoles(userDetails.role);
  const canEditAcademicYear = hasAllowedRole(ROLE_GROUPS.admin, userRoles);
  const canOpenSchoolSettings = hasAllowedRole(ROLE_GROUPS.admin, userRoles);
  const canOpenAdminPanel = hasAllowedRole(ROLE_GROUPS.superAdminOnly, userRoles);

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          width: 320,
          mt: 1.5,
          borderRadius: 1.5,
          boxShadow: '0 20px 44px rgba(15, 23, 42, 0.14)',
        },
      }}
    >
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Avatar
          alt={getFullName()}
          src={userDetails.avatarUrl || '/static/images/avatar/default-avatar.png'}
          sx={{ width: 60, height: 60, mb: 1, mx: 'auto' }}
        />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {getFullName()}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {formatRoleName(userDetails.role)}
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ px: 2, py: 1.25 }}>
        <Stack spacing={1}>
          {canEditAcademicYear && (
            <Box
              sx={{
                px: 1.4,
                py: 1,
                borderRadius: 2,
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Academic Year
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {academicYear || 'N/A'}
                </Typography>
              </Box>
              <ListItemButton
                onClick={onAcademicYearClick}
                sx={{
                  minHeight: 30,
                  px: 1.1,
                  borderRadius: 1.5,
                  flex: '0 0 auto',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  Edit
                </Typography>
              </ListItemButton>
            </Box>
          )}

          <Box
            sx={{
              px: 1.4,
              py: 0.9,
              borderRadius: 2,
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DarkModeRoundedIcon color="primary" fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Dark mode
              </Typography>
            </Box>
            <Switch checked={isDarkMode} onChange={onThemeToggle} size="small" />
          </Box>
        </Stack>
      </Box>
      <Divider />
      <List>
        <ListItemButton
          onClick={onNotificationsOpen}
          sx={{ py: 1.5 }}
        >
          <ListItemIcon>
            <Badge
              color="error"
              badgeContent={notificationCount}
              max={9}
              invisible={!notificationCount}
            >
              <NotificationsIcon color="primary" fontSize="small" />
            </Badge>
          </ListItemIcon>
          <ListItemText primary={<Typography variant="body1">Notifications</Typography>} />
        </ListItemButton>

        <ListItemButton
          onClick={() => {
            navigate('/account');
            onClose();
          }}
          sx={{ py: 1.5 }}
        >
          <ListItemIcon>
            <AccountCircleIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={<Typography variant="body1">Account</Typography>} />
        </ListItemButton>

        {canOpenSchoolSettings && (
          <ListItemButton
            onClick={() => {
              navigate('/school-settings');
              onClose();
            }}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon>
              <SettingsIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={<Typography variant="body1">Settings</Typography>} />
          </ListItemButton>
        )}

        {canOpenAdminPanel && (
          <ListItemButton
            onClick={() => {
              navigate('/admin/settings');
              onClose();
            }}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon>
              <SchoolIcon color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={<Typography variant="body1">Admin Panel</Typography>} />
          </ListItemButton>
        )}
      </List>
      <Divider />
      <ListItemButton onClick={onLogoutClick} sx={{ py: 1.5 }}>
        <ListItemIcon>
          <LogoutIcon color="error" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={<Typography variant="body1">Logout</Typography>} />
      </ListItemButton>
    </Popover>
  );
};

UserMenuPopover.propTypes = {
  anchorEl: PropTypes.any,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userDetails: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    role: PropTypes.string,
    avatarUrl: PropTypes.string,
  }).isRequired,
  onLogoutClick: PropTypes.func.isRequired,
  academicYear: PropTypes.string,
  notificationCount: PropTypes.number,
  onThemeToggle: PropTypes.func.isRequired,
  isDarkMode: PropTypes.bool.isRequired,
  onNotificationsOpen: PropTypes.func.isRequired,
  onAcademicYearClick: PropTypes.func.isRequired,
};

export default UserMenuPopover;
